/**
 * PulseFit View Router Coordinator
 * 
 * Manages view transitions, tab synchronization, auto-pausing guided workouts,
 * and dispatching view activation hooks.
 */

import { store } from '../../store.js';
import { renderPerformanceView } from './performance.js';
import { populateSettingsGoalsFields, setupGemini, updateStravaVisibility, setupStrava, updateStravaUI } from './settings.js';
import { renderDashboard, renderAchievements } from './dashboard.js';
import { renderWorkouts } from './workouts.js';
import { renderReleaseNotes } from './releasenotes.js';
import { initComparisonView } from './comparison.js';


let previousViewId = 'view-dashboard';

export function getPreviousViewId() {
  return previousViewId;
}

export function setPreviousViewId(viewId) {
  previousViewId = viewId;
}

export function showView(viewId, options = {}) {
  if (typeof document === 'undefined') return;

  const sections = document.querySelectorAll('.content-view');
  const navItems = document.querySelectorAll('.nav-item');
  
  // Auto-pause guided workout if navigating away
  if (viewId !== 'view-guided-workout' && typeof window !== 'undefined' && typeof window.guidedWorkoutIsPaused !== 'undefined') {
    if (window.guidedWorkoutInterval && !window.guidedWorkoutIsPaused) {
      window.guidedWorkoutIsPaused = true;
      if (typeof window.updateGuidedWorkoutPlayPauseUI === 'function') {
        window.updateGuidedWorkoutPlayPauseUI();
      }
      if (typeof window.playGuidedBeep === 'function') {
        window.playGuidedBeep(550, 150);
      }
    }
  }

  // Track previous view before navigating away (unless it's the detail view itself)
  const currentActiveSec = document.querySelector('.content-view.active');
  if (currentActiveSec && currentActiveSec.id !== 'view-workout-detail') {
    previousViewId = currentActiveSec.id;
  }
  
  // Remove active states from other sections (avoid collapsing active view scroll state)
  sections.forEach(sec => {
    if (sec.id !== viewId) {
      sec.classList.remove('active');
    }
  });
  navItems.forEach(nav => nav.classList.remove('active'));
  
  const targetSec = document.getElementById(viewId);
  if (targetSec) {
    targetSec.classList.add('active');
    
    // Dispatch view activation hooks
    if (viewId === 'view-performance') {
      if (typeof window !== 'undefined' && typeof window.renderPerformanceView === 'function') {
        window.renderPerformanceView();
      } else if (typeof renderPerformanceView === 'function') {
        renderPerformanceView();
      }
    } else if (viewId === 'view-settings') {
      if (typeof window !== 'undefined' && typeof window.populateSettingsGoalsFields === 'function') {
        window.populateSettingsGoalsFields();
      } else if (typeof populateSettingsGoalsFields === 'function') {
        populateSettingsGoalsFields();
      }
      if (typeof window !== 'undefined' && typeof window.setupGemini === 'function') {
        window.setupGemini();
      } else if (typeof setupGemini === 'function') {
        setupGemini();
      }
      if (typeof window !== 'undefined' && typeof window.updateStravaVisibility === 'function') {
        window.updateStravaVisibility();
      } else if (typeof updateStravaVisibility === 'function') {
        updateStravaVisibility();
      }
      if (typeof window !== 'undefined' && typeof window.setupStrava === 'function') {
        window.setupStrava();
      } else if (typeof setupStrava === 'function') {
        setupStrava();
      }
      if (typeof window !== 'undefined' && typeof window.updateStravaUI === 'function') {
        window.updateStravaUI();
      } else if (typeof updateStravaUI === 'function') {
        updateStravaUI();
      }
    } else if (viewId === 'view-dashboard') {
      if (typeof window !== 'undefined' && typeof window.renderDashboard === 'function') {
        window.renderDashboard();
      } else if (typeof renderDashboard === 'function') {
        renderDashboard();
      }
    } else if (viewId === 'view-workouts') {
      if (typeof window !== 'undefined' && typeof window.renderWorkouts === 'function') {
        window.renderWorkouts();
      } else if (typeof renderWorkouts === 'function') {
        renderWorkouts();
      }
    } else if (viewId === 'view-achievements') {
      if (typeof window !== 'undefined' && typeof window.renderAchievements === 'function') {
        window.renderAchievements();
      } else if (typeof renderAchievements === 'function') {
        renderAchievements();
      }
    } else if (viewId === 'view-release-notes') {
      if (typeof window !== 'undefined' && typeof window.renderReleaseNotes === 'function') {
        window.renderReleaseNotes();
      } else if (typeof renderReleaseNotes === 'function') {
        renderReleaseNotes();
      }
    } else if (viewId === 'view-workout-comparison') {
      if (typeof window !== 'undefined' && typeof window.initComparisonView === 'function') {
        window.initComparisonView();
      } else if (typeof initComparisonView === 'function') {
        initComparisonView();
      }
    }

  }
  
  // Highlight corresponding sidebar link
  const tabId = viewId.replace('view-', '');
  const matchingNav = document.querySelector(`.nav-item[href="#${tabId}"]`);
  if (matchingNav) {
    matchingNav.classList.add('active');
  }
  
  if (!options || !options.preserveScroll) {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) mainContent.scrollTop = 0;
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo(0, 0);
    }
  }

  if (store) {
    store.activeView = viewId;
  }
}

export function setupNavigation() {
  if (typeof document === 'undefined') return;
  const navItems = document.querySelectorAll('.nav-item');
  
  navItems.forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const href = item.getAttribute('href') || '';
      const targetId = href.startsWith('#') ? href.substring(1) : href;
      showView(`view-${targetId}`);
    });
  });
}

export function setupConnectivityListeners() {
  if (typeof window === 'undefined') return;

  window.addEventListener('offline', () => {
    if (typeof window.showToast === 'function') {
      window.showToast('You are offline. Showing cached workouts.');
    }
  });

  window.addEventListener('online', () => {
    if (typeof window.showToast === 'function') {
      window.showToast('Connection restored.');
    }
    if (typeof window.fetchUserWorkouts === 'function') {
      window.fetchUserWorkouts();
    }
  });
}

let isRouterInitialized = false;

export function initRouter() {
  if (isRouterInitialized) return;
  isRouterInitialized = true;
  setupNavigation();
  setupConnectivityListeners();
}

